package top.wcpe.allincore.ui.runtime.internal.schedule

import top.wcpe.allincore.ui.runtime.ir.ScheduledTaskHandle

// 浏览器 / Node 全局定时器（返回值两端类型不同：浏览器 number / Node Timeout 对象，统一按 dynamic 持有）
private external fun setTimeout(handler: () -> Unit, timeout: Int): dynamic

private external fun clearTimeout(handle: dynamic)

private external fun setInterval(handler: () -> Unit, timeout: Int): dynamic

private external fun clearInterval(handle: dynamic)

/** 毫秒 Long → setTimeout 的 Int 参数（负值归零，超界钳到 Int.MAX_VALUE）。 */
private fun clampMillis(ms: Long): Int = ms.coerceIn(0L, Int.MAX_VALUE.toLong()).toInt()

/**
 * JS：事件循环上的 setTimeout/setInterval。单线程无线程概念，[threadName] 仅为对齐 expect 签名。
 * shutdownNow 清掉所有在挂定时器并拒绝后续提交。
 */
internal actual class PlatformScheduler actual constructor(@Suppress("UNUSED_PARAMETER") threadName: String) {

    private var closed = false

    /** 在挂句柄集合：shutdownNow 时统一清理；触发 / 取消时自移除。 */
    private val live = LinkedHashSet<JsHandle>()

    actual fun execute(task: () -> Unit): Boolean {
        if (closed) return false
        // 对齐 JVM「提交到执行线程」的异步语义：不在当前调用栈内执行
        setTimeout({ if (!closed) task() }, 0)
        return true
    }

    actual fun schedule(task: () -> Unit, delayMs: Long): ScheduledTaskHandle? {
        if (closed) return null
        val handle = JsHandle()
        handle.timeoutId = setTimeout({
            live.remove(handle)
            task()
        }, clampMillis(delayMs))
        live.add(handle)
        return handle
    }

    actual fun scheduleRepeating(task: () -> Unit, initialMs: Long, periodMs: Long): ScheduledTaskHandle? {
        if (closed) return null
        val handle = JsHandle()
        val period = clampMillis(periodMs).coerceAtLeast(1)
        // 首跳走 setTimeout（自定义首次延迟），之后转 setInterval 周期触发
        handle.timeoutId = setTimeout({
            handle.timeoutId = null
            if (!handle.cancelled) {
                task()
                if (!handle.cancelled) {
                    handle.intervalId = setInterval({ task() }, period)
                }
            }
        }, clampMillis(initialMs))
        live.add(handle)
        return handle
    }

    actual fun shutdownNow() {
        closed = true
        for (h in live.toList()) {
            h.cancel()
        }
        live.clear()
    }

    /** 定时器句柄：同时持有可能存在的 timeout / interval id，cancel 幂等。 */
    private inner class JsHandle : ScheduledTaskHandle {
        var cancelled = false
        var timeoutId: dynamic = null
        var intervalId: dynamic = null

        override fun cancel() {
            if (cancelled) return
            cancelled = true
            if (timeoutId != null) clearTimeout(timeoutId)
            if (intervalId != null) clearInterval(intervalId)
            live.remove(this)
        }
    }
}
